import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { toastError } from '@telialt/common-toast/src';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';

import { MUTATION_KEYS, PLAN_SUMMARY_TOAST_ID } from './constants';

/**
 * Hook handles Plan Summary file downloading or provides a download link for anchor tag.
 *
 * @param {() => Promise<string>} mutationFn - Api function that returns downloadable files url.
 * @param {string[]} mutationKeyUnique - append unique values to mutation key to avoid caching issues.
 * @param {{ errorMessage: string; errorClose: string }} texts - Optional texts for Error-Toast.
 * @returns {{
 *   fileDownloadLink: string | null; // download link is provided only when new window was blocked (meaning automatic download failed)
 *   isPending: boolean;
 *   download: () => void; // triggers the download
 * }}
 */
export const useDownloadFileOrGiveDownloadLink = (
    mutationFn: () => Promise<string>,
    mutationKeyUnique: Array<unknown>,
    texts?: { errorMessage?: string; errorClose?: string },
): { fileDownloadLink: string | null; isPending: boolean; download: () => void } => {
    const { t } = useTranslations();
    const errorMessage = texts?.errorMessage || t['common.api.error.default'];
    const errorClose = texts?.errorClose || t['common.button.close'];
    const showErrorToast = () => toastError(errorMessage, { toastId: PLAN_SUMMARY_TOAST_ID, buttonText: errorClose });

    const [fileDownloadLink, setFileDownloadLink] = useState<string>('');

    const {
        mutate: triggerDownload,
        error: mutationError,
        isPending,
    } = useMutation({
        mutationKey: [MUTATION_KEYS.CONTRACT_SUMMARY, ...mutationKeyUnique],
        mutationFn,
        onSuccess: (url: string) => {
            if (!url) {
                showErrorToast();

                return;
            }
            if (!window.open(url, '_blank')) {
                // if new window was blocked, hook should return download link, to be used in anchor tag
                setFileDownloadLink(url);
            }
        },
        onError: showErrorToast,
    });

    useLogError(mutationError, DDComponent.Plans);

    return {
        fileDownloadLink,
        isPending,
        download: () => triggerDownload(),
    };
};
